import React, { FunctionComponent, useEffect, useState } from 'react';

import { clone, forEach, merge } from 'lodash';

import {
    DropdownPicker,
    Props as DropdownPickerProps,
    DropdownDataWithHeader,
} from 'common/dropdown-picker/DropdownPicker';
import { Emojis, EmojiCategories } from 'common/emojis/Emoji';

import 'common/emojis/emoji-picker/EmojiPicker.scss';

interface Handlers {
    onSelect: any;
    onClose?: any;
    onDataLoad?: any;
}

export interface Props extends Omit<DropdownPickerProps, 'dropdownData' | 'handlers'> {
    dropdownRef?: any;
    handlers?: Handlers;
}

export const EmojiPicker: FunctionComponent<Props> = ({
    dropdownRef,
    handlers,
    searchOptions,
    displayOptions,
    enableAutoSelect,
    autoFocus,
}): JSX.Element => {
    const [hasDataLoaded, setHasDataLoaded] = useState(false);
    const [emojiData, setEmojiData] = useState([]);

    const mergedDisplayOptions = merge(clone(EmojiPicker.defaultProps?.displayOptions), displayOptions);

    useEffect(() => {
        const getUnicodeStr = (unicode: string): any => {
            return String.fromCodePoint(
                ...unicode.split('-').map((value: any): any => {
                    return parseInt(value, 16);
                }),
            );
        };
        const getEmojiData = (): DropdownDataWithHeader[] => {
            const categories: any = {};

            forEach(EmojiCategories, (categoryItem: any): void => {
                if (!categories[categoryItem.id]) {
                    categories[categoryItem.id] = categoryItem;
                }
            });
            const dataByCategory: any = {};
            const dropdownData: DropdownDataWithHeader[] = [];

            forEach(Emojis, (emojiItem: any): void => {
                if (!dataByCategory[emojiItem.category]) {
                    dataByCategory[emojiItem.category] = {
                        id: emojiItem.category,
                        header: categories[emojiItem.category].name,
                        icon: categories[emojiItem.category].icon,
                        content: [],
                    };
                }
                dataByCategory[emojiItem.category].content.push({
                    id: emojiItem.name,
                    label: getUnicodeStr(emojiItem.unicode),
                    description: emojiItem.name,
                    additionalInfo: {
                        unicode: emojiItem.unicode,
                        decimal: emojiItem.decimal,
                    },
                });
            });
            forEach(dataByCategory, (categoryItem: any): void => {
                dropdownData.push(categoryItem);
            });
            return dropdownData;
        };
        const data = getEmojiData();
        const timer = setTimeout(() => {
            setHasDataLoaded(true);
            setEmojiData(data as any);
            if (!autoFocus && handlers && handlers.onDataLoad) {
                handlers.onDataLoad();
            }
        }, 0);

        return (): void => clearTimeout(timer);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSelection = (value: DropdownDataWithHeader): void => {
        if (handlers && handlers!.onSelect) {
            handlers!.onSelect(value);
        }
    };

    const handleClose = (): void => {
        if (handlers && handlers!.onClose) {
            handlers!.onClose();
        }
    };

    return (
        <div className="r4e-emoji-picker">
            <DropdownPicker
                ref={dropdownRef}
                searchOptions={searchOptions}
                dropdownData={emojiData}
                hasDataLoaded={hasDataLoaded}
                enableAutoSelect={enableAutoSelect}
                hasOverlayPromise
                autoFocus={autoFocus}
                displayOptions={mergedDisplayOptions}
                handlers={{
                    onSelect: handleSelection,
                    onOutsideClick: handleClose,
                }}
            />
        </div>
    );
};

EmojiPicker.defaultProps = {
    displayOptions: {
        maxHeight: '300px',
        displayDescription: false,
        displayFooter: true,
        displayCategories: true,
        countPerRow: 9,
        reviseMaxHeight: false,
    },
};
